import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TAddDeviceFormProps } from '@/console/features/devices/components/device-drawer/add-device-form.types';
import { TAddDeviceInputSchema } from '@/console/features/devices/components/device-drawer/add-device.types';
import { DeviceSelect } from '@/console/features/devices/components/device-select/device-select';
import { useDeviceSelectOptions } from '@/console/features/devices/hooks/use-device-select-options/use-device-select-options';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import {
  FormContainer,
  FormContent,
} from '@/shared/components/ui/form/form.styled';
import { SelectedItemsList } from '@/shared/components/ui/selected-items-list/selected-items-list';
import { DeviceFragment } from '@/shared/types/types.generated';

export const AddDeviceForm: React.FC<TAddDeviceFormProps> = ({
  onClose,
  groupId,
}) => {
  const { t } = useTranslation();
  const { setValue, control, trigger } =
    useFormContext<TAddDeviceInputSchema>();
  const { createDeviceOptions } = useDeviceSelectOptions({
    additionalInfo: { deviceType: true, vendor: true },
  });

  const devices = useWatch({
    control,
    name: 'devices',
    defaultValue: [],
  });

  const getSelectedItems = () => {
    return devices?.map((device) => ({
      icon: <DevicesIcon />,
      name: device.name,
      onDelete: () => {
        setValue(
          'devices',
          devices?.filter(({ deviceId }) => deviceId !== device.deviceId),
        );
        void trigger('devices');
      },
    }));
  };

  const deviceFilter = (device: DeviceFragment) =>
    !groupId || device?.groupId !== groupId;

  return (
    <FormContainer justifyContent="space-between">
      <FormContent>
        <DeviceSelect<TAddDeviceInputSchema, DeviceFragment>
          name="devices"
          deviceFilter={deviceFilter}
          createOption={createDeviceOptions}
          getItemId={(value) => value?.deviceId}
          required
        />
        <SelectedItemsList
          title={t('device:selected_devices')}
          selectedItems={getSelectedItems()}
        />
      </FormContent>
      <Submit onClose={onClose} submitButtonLabel={t('common:add')} />
    </FormContainer>
  );
};
